import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Wrapper } from "./Wrapper";
import moment from "moment-timezone";
import { ToastContainer, toast } from "react-toastify";
import { payoutService } from "../../_services/payout.service";
import ApexCharts from "react-apexcharts";
import { propertyService } from "../../_services";
import { useAdminPayoutOperation } from "../../hooks/useAdminPayoutOperation";
import {
  buildFinanceSummaryRequest,
  formatCents,
  getFinanceSummaryErrorMessage,
  normalizeFinanceSummaryResponse,
  normalizeCommissionActivityResponse,
  reconciliationDetails,
  payoutIsBlocked,
} from "./dashboardFinanceUtils";

function createRefundOperationKey() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return `refund-${Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("")}`;
}

function Earnings() {
  const [isWalletFetching, setIsWalletFetching] = useState(true);
  const [page, setPage] = useState(1);
  const [activity, setActivity] = useState(null);
  const [activityError, setActivityError] = useState("");
  const [activityLoading, setActivityLoading] = useState(true);
  const [activityDateFilter, setActivityDateFilter] = useState("all");
  const [activityFromDate, setActivityFromDate] = useState("");
  const [activityToDate, setActivityToDate] = useState("");
  const [activityRange, setActivityRange] = useState({});
  const activityTokenRef = useRef(0);
  const activitySnapshotRef = useRef({ key: null, asOf: null });
  const [refreshKey, setRefreshKey] = useState(0);
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");
  const [isLoadingSearchResult, setLoadingSearchResult] = useState(false);
  const [financeSummary, setFinanceSummary] = useState(null);
  const [financeError, setFinanceError] = useState("");
  const [isSummaryStale, setIsSummaryStale] = useState(false);
  const [selectedGraph, setSelectedGraph] = useState("last7DaysEarnings");
  const requestTokenRef = useRef(0);
  const summaryRef = useRef(null);
  const refreshFinanceSummary = useCallback(
    () => setRefreshKey((current) => current + 1),
    []
  );
  const {
    isPayoutActive,
    isRequesting: isRequestingPayout,
    operation: payoutOperation,
    requestPayout,
  } = useAdminPayoutOperation(refreshFinanceSummary);

  // --- REFUND MODAL STATES ---
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [selectedRefundItem, setSelectedRefundItem] = useState(null);
  const [refundAmount, setRefundAmount] = useState("");
  const [refundStep, setRefundStep] = useState(1); // 1: Input, 2: Confirmation
  const [refundError, setRefundError] = useState("");
  const [isSubmittingRefund, setIsSubmittingRefund] = useState(false);
  const [refundOperationKey, setRefundOperationKey] = useState(null);

  const [graphFromDate, setGraphFromDate] = useState(
    moment().tz("America/Chicago").subtract(6, "days").format("YYYY-MM-DD")
  );
  const [graphToDate, setGraphToDate] = useState(
    moment().tz("America/Chicago").format("YYYY-MM-DD")
  );

  const chartOptions = useMemo(() => {
    const points = financeSummary?.graph?.points || [];
    return {
      series: [
        {
          name: "Recorded platform net revenue",
          data: points.map((point) => point.valueCents / 100),
        },
      ],
      options: {
        chart: {
          height: 350,
          type: "area",
          toolbar: { show: false },
        },
        dataLabels: { enabled: false },
        stroke: { curve: "smooth" },
        xaxis: { categories: points.map((point) => point.period) },
        yaxis: {
          labels: {
            formatter: (value) =>
              new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: String(financeSummary?.currency || "usd").toUpperCase(),
                maximumFractionDigits: 0,
              }).format(value),
          },
        },
      },
    };
  }, [financeSummary]);

  const fetchFinanceSummary = useCallback(async () => {
    const requestToken = ++requestTokenRef.current;
    setIsWalletFetching(true);
    try {
      const request = buildFinanceSummaryRequest({
        graphFilter: selectedGraph,
        page: 1,
        limit: 10,
        fromDate: graphFromDate,
        toDate: graphToDate,
      });
      const response = await payoutService.getFinanceSummary(request);
      const summary = normalizeFinanceSummaryResponse(response);
      if (requestToken !== requestTokenRef.current) return;
      summaryRef.current = summary;
      setFinanceSummary(summary);
      setFinanceError("");
      setIsSummaryStale(false);
    } catch (error) {
      if (requestToken !== requestTokenRef.current) return;
      setFinanceError(getFinanceSummaryErrorMessage(error));
      setIsSummaryStale(Boolean(summaryRef.current));
    } finally {
      if (requestToken === requestTokenRef.current) {
        setIsWalletFetching(false);
      }
    }
  }, [
    graphFromDate,
    graphToDate,
    selectedGraph,
  ]);

  useEffect(() => {
    fetchFinanceSummary();
    return () => {
      requestTokenRef.current += 1;
    };
  }, [fetchFinanceSummary, refreshKey]);


  useEffect(() => {
    const token = ++activityTokenRef.current;
    const key = JSON.stringify([debouncedSearchValue, activityRange, refreshKey]);
    if (activitySnapshotRef.current.key !== key) activitySnapshotRef.current = { key, asOf: null };
    setActivityLoading(true);
    setActivityError("");
    payoutService.getCommissionActivity({ page, limit: 10, reservationId: debouncedSearchValue,
      ...activityRange, asOf: activitySnapshotRef.current.asOf || undefined })
      .then(normalizeCommissionActivityResponse)
      .then((result) => {
        if (token !== activityTokenRef.current) return;
        activitySnapshotRef.current.asOf = result.asOf;
        setActivity(result);
      })
      .catch((error) => {
        if (token !== activityTokenRef.current) return;
        setActivityError(getFinanceSummaryErrorMessage(error));
      })
      .finally(() => { if (token === activityTokenRef.current) setActivityLoading(false); });
    return () => { activityTokenRef.current += 1; };
  }, [page, debouncedSearchValue, activityRange, refreshKey]);

  const applyActivityDates = () => {
    if (!activityFromDate || !activityToDate || activityFromDate > activityToDate) {
      setActivityError("Choose both activity dates, with From on or before To.");
      return;
    }
    setPage(1);
    setActivityRange({ fromDate: activityFromDate, toDate: activityToDate });
  };

  const handleNextPage = () => {
    setPage(page + 1);
  };

  const handlePrevPage = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };
  const handleOnSearch = (e) => {
    setSearchValue(e.target.value);
  };

  useEffect(() => {
    if (searchValue.trim() === debouncedSearchValue) {
      setLoadingSearchResult(false);
      return undefined;
    }
    setLoadingSearchResult(true);
    const getSearchResult = setTimeout(() => {
      setPage(1);
      setDebouncedSearchValue(searchValue.trim());
      setLoadingSearchResult(false);
    }, 2000);

    return () => clearTimeout(getSearchResult);
  }, [debouncedSearchValue, searchValue]);

  const handleRequestPayoutForAdmin = async () => {
    if (financeActionsUnavailable || payoutIsBlocked(financeSummary)) return;
    try {
      const result = await requestPayout();
      if (result.data.data && result.data.data.state === "succeeded") {
        toast.success(result.data.message);
      } else {
        toast.info(result.data.message);
      }
    } catch (error) {
      toast.error(
        (error.response && error.response.data && error.response.data.message) ||
          "Unable to request payout."
      );
    }
  };


  const openRefundModal = (item) => {
    if (refundActionsUnavailable) return;
    if (
      !item?.id ||
      !Number.isSafeInteger(item.finalAmountCents) ||
      !Number.isSafeInteger(item.processingFeesCents)
    ) {
      toast.error("This record does not contain enough finance data to refund safely.");
      return;
    }
    setSelectedRefundItem(item);
    setRefundAmount("");
    setRefundStep(1);
    setRefundError("");
    setRefundOperationKey(createRefundOperationKey());
    setShowRefundModal(true);
  };

  const closeRefundModal = () => {
    setShowRefundModal(false);
    setSelectedRefundItem(null);
    setRefundAmount("");
    setRefundStep(1);
    setRefundError("");
    setIsSubmittingRefund(false);
    setRefundOperationKey(null);
  };

  const handleRefundAmountChange = (e) => {
    setRefundAmount(e.target.value);
    setRefundError("");
  };

  const handleProceedToConfirm = () => {
    const numericAmount = Number(refundAmount);
    const amountCents = Math.round(numericAmount * 100);
    if (
      !refundAmount ||
      !Number.isFinite(numericAmount) ||
      !Number.isSafeInteger(amountCents) ||
      Math.abs(numericAmount * 100 - amountCents) > 0.000001
    ) {
      setRefundError("Please enter a valid number.");
      return;
    }

    if (amountCents <= 0) {
      setRefundError("Amount must be greater than 0.");
      return;
    }

    const refundableCents =
      (selectedRefundItem?.finalAmountCents || 0) +
      (selectedRefundItem?.processingFeesCents || 0);
    if (selectedRefundItem && amountCents > refundableCents) {
      setRefundError(
        `Amount cannot exceed ${formatCents(
          refundableCents,
          financeSummary?.currency
        )}.`
      );
      return;
    }

    setRefundStep(2);
  };

  const handleSubmitRefund = async () => {
    if (!selectedRefundItem || refundActionsUnavailable) return;

    setIsSubmittingRefund(true);
    try {
      const res = await propertyService.partialRefund({
        commissionId: selectedRefundItem.id,
        amount: Math.round(Number(refundAmount) * 100) / 100,
        idempotencyKey: refundOperationKey,
      });

      if (res.status && res.data?.state === "succeeded") {
        toast.success("Partial refund processed successfully!");
        closeRefundModal();
        refreshFinanceSummary();
      } else if (res.status && res.data?.state) {
        const stateLabel = String(res.data.state).replaceAll("_", " ");
        setRefundError(`Refund is ${stateLabel}; the dashboard has not reported it as completed.`);
        toast.info(`Refund is ${stateLabel}.`);
      } else {
        toast.error("Something went wrong");
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to process refund.");
    } finally {
      setIsSubmittingRefund(false);
    }
  };

  const commissions = activity?.records || [];
  const activityPagination = activity?.pagination || {};
  const financeActionsUnavailable = isWalletFetching || isSummaryStale || Boolean(financeError) || !financeSummary;
  const refundActionsUnavailable = financeActionsUnavailable || activityLoading || isLoadingSearchResult || Boolean(activityError);
  const reasons = reconciliationDetails(financeSummary);
  const selectedRefundableCents = selectedRefundItem
    ? (selectedRefundItem.finalAmountCents || 0) +
      (selectedRefundItem.processingFeesCents || 0)
    : 0;
  const businessTimezone = financeSummary?.timezone || "America/Chicago";
  const businessToday = moment().tz(businessTimezone).format("YYYY-MM-DD");

  return (
    <div>
      <ToastContainer />
      <Wrapper>
        <>
            {isWalletFetching && <p role="status">Loading finance summary…</p>}
            {financeError && (
              <div
                className={`alert ${
                  isSummaryStale ? "alert-warning" : "alert-danger"
                }`}
                role="alert"
              >
                {financeError}
                <button className="btn btn-link" onClick={refreshFinanceSummary}>Retry finance summary</button>
                {isSummaryStale &&
                  " Showing the last completed finance snapshot."}
              </div>
            )}
            <div className="row justify-content-center gap-4 mb-5">
              <div className="d-flex dashboard-container gap-3">
                <div className="col-3 fristcol">
                  <div className="align-items-center d-flex flex-column gap-2 justify-content-center mb-3 p-4 cs-card cs-card-height w-4">
                    <p className="card-text">Host paid</p>
                    <h5 className="card-title mb-0">
                      {formatCents(
                        financeSummary?.metrics?.hostPaidCents,
                        financeSummary?.currency
                      )}
                    </h5>
                    {Number.isSafeInteger(financeSummary?.metrics?.hostTransferredToConnectedCents) && <>
                      <p className="card-text">Verified transfers to host Stripe accounts</p>
                      <h5 className="card-title mb-0">{formatCents(financeSummary.metrics.hostTransferredToConnectedCents, financeSummary.currency)}</h5>
                      <small>Transfers are separate from confirmed bank payouts.</small>
                    </>}
                    <p className="card-text">Host available</p>
                    <h5 className="card-title mb-0">
                      {formatCents(
                        financeSummary?.metrics?.hostAvailableCents,
                        financeSummary?.currency
                      )}
                    </h5>
                    <p className="card-text">Host owed</p>
                    <h5 className="card-title mb-0">
                      {formatCents(
                        financeSummary?.metrics?.hostOwedCents,
                        financeSummary?.currency
                      )}
                    </h5>
                    <p className="card-text">Host liability held by platform</p>
                    <h5 className="card-title mb-0">
                      {formatCents(
                        financeSummary?.metrics?.hostPlatformHeldLiabilityCents,
                        financeSummary?.currency
                      )}
                    </h5>
                    <p className="card-text">Host reserved</p>
                    <h5 className="card-title mb-0">
                      {formatCents(
                        financeSummary?.metrics?.hostReservedCents,
                        financeSummary?.currency
                      )}
                    </h5>
                    <p className="card-text">Host in transit</p>
                    <h5 className="card-title mb-0">
                      {formatCents(
                        financeSummary?.metrics?.hostInTransitCents,
                        financeSummary?.currency
                      )}
                    </h5>
                  </div>
                  <div className="align-items-center d-flex flex-column gap-2 justify-content-center p-4 cs-card cs-card-height w-4">
                    <p className="card-text">Platform net revenue</p>
                    <h5
                      className="card-title"
                      data-testid="platform-net-revenue"
                    >
                      {formatCents(
                        financeSummary?.metrics
                          ?.recordedPlatformNetRevenueCents,
                        financeSummary?.currency
                      )}
                    </h5>
                    <p className="card-text">
                      Platform cash before payout fees
                    </p>
                    <h5 className="card-title">
                      {formatCents(
                        financeSummary?.metrics
                          ?.platformAvailableCashBeforePayoutFeesCents,
                        financeSummary?.currency
                      )}
                    </h5>
                    <p className="card-text">Stripe available cash</p>
                    <h5 className="card-title">
                      {formatCents(
                        financeSummary?.metrics?.stripeAvailableCashCents,
                        financeSummary?.currency
                      )}
                    </h5>

                    <small>Cash held by Stripe is separate from revenue and the amount eligible for payout.</small>
                    {financeSummary && (
                      <button
                        onClick={handleRequestPayoutForAdmin}
                        className="btn btn-secondary btn-sm py-1"
                        disabled={
                          financeActionsUnavailable ||
                          payoutIsBlocked(financeSummary) ||
                          isRequestingPayout ||
                          isPayoutActive
                        }
                      >
                        {isRequestingPayout || isPayoutActive
                          ? "Payout Processing"
                          : "Request Payout"}
                      </button>
                    )}
                    {financeSummary?.reconciliation?.hostPayoutBlocked && (
                      <p className="text-warning mb-0">
                        Host reconciliation blocks payout.
                        {Number.isSafeInteger(financeSummary.reconciliation.hostRequiresReconciliationCount) && ` ${financeSummary.reconciliation.hostRequiresReconciliationCount} host(s) need review.`}
                      </p>
                    )}
                    {financeSummary?.reconciliation
                      ?.providerFinancePayoutBlocked && (
                      <p className="text-warning mb-0">
                        Provider reconciliation blocks payout
                      </p>
                    )}
                    {payoutOperation &&
                      payoutOperation.requiresManualReconciliation && (
                        <p className="text-warning mb-0">
                          Payout reconciliation in progress
                        </p>
                      )}
                  </div>
                </div>
                <div className="col-9 seccol d-flex flex-column gap-2  p-4 cs-card">
                  <div>Recorded platform finance summary</div>
                  <div className="d-flex gap-4">
                    <div>
                      Last 7 days
                      <h5 className="card-title">
                        {formatCents(
                          financeSummary?.comparisons?.last7Days
                            ?.recordedPlatformNetRevenueCents,
                          financeSummary?.currency
                        )}
                      </h5>
                    </div>
                    <div>
                      Last 30 days
                      <h5 className="card-title">
                        {formatCents(
                          financeSummary?.comparisons?.last30Days
                            ?.recordedPlatformNetRevenueCents,
                          financeSummary?.currency
                        )}
                      </h5>
                    </div>
                    <div>
                      Year to Date
                      <h5 className="card-title">
                        {formatCents(
                          financeSummary?.comparisons?.ytd
                            ?.recordedPlatformNetRevenueCents,
                          financeSummary?.currency
                        )}
                      </h5>
                    </div>
                  </div>
                  <div className="d-flex gap-4">
                    <div>
                      Gross collections
                      <h6>
                        {formatCents(
                          financeSummary?.metrics
                            ?.recordedGrossCollectionsCents,
                          financeSummary?.currency
                        )}
                      </h6>
                    </div>
                    <div>
                      Refunds
                      <h6>
                        {formatCents(
                          financeSummary?.metrics?.recordedRefundsCents,
                          financeSummary?.currency
                        )}
                      </h6>
                    </div>
                    <div>
                      Processing fees
                      <h6>
                        {formatCents(
                          Number.isSafeInteger(
                            financeSummary?.metrics
                              ?.recordedProcessingFeesCents
                          )
                            ? -Math.abs(
                                financeSummary.metrics
                                  .recordedProcessingFeesCents
                              )
                            : null,
                          financeSummary?.currency
                        )}
                      </h6>
                    </div>
                    <div>
                      Dispute net impact
                      <h6>
                        {formatCents(
                          financeSummary?.metrics?.disputeNetImpactCents,
                          financeSummary?.currency
                        )}
                      </h6>
                    </div>
                  </div>
                  <div className="d-flex gap-4">
                    <div>
                      Platform gross revenue
                      <h6>
                        {formatCents(
                          financeSummary?.metrics
                            ?.recordedPlatformGrossRevenueCents,
                          financeSummary?.currency
                        )}
                      </h6>
                    </div>
                    <div>
                      Platform refund adjustments
                      <h6>
                        {formatCents(
                          financeSummary?.metrics
                            ?.recordedPlatformRefundAdjustmentsCents,
                          financeSummary?.currency
                        )}
                      </h6>
                    </div>
                    <div>
                      Reconciliation variance
                      <h6>
                        {formatCents(
                          financeSummary?.metrics?.reconciliationVarianceCents,
                          financeSummary?.currency
                        )}
                      </h6>
                    </div>
                  </div>
                  <div className="flex-1">
                    {/* <Chart
                      chartType="AreaChart"
                      width="100%"
                      height="200px"
                      data={data}
                      options={options}
                    /> */}
                    <ApexCharts
                      options={chartOptions.options}
                      series={chartOptions.series}
                      type="area"
                      height={200}
                    />
                  </div>
                  <div className="d-flex justify-content-between">
                    <div>
                      <select
                        onChange={(e) => {
                          const nextFilter = e.target.value;
                          if (nextFilter === "custom") {
                            setGraphToDate(businessToday);
                            setGraphFromDate(
                              moment
                                .tz(businessToday, "YYYY-MM-DD", businessTimezone)
                                .subtract(6, "days")
                                .format("YYYY-MM-DD")
                            );
                          }
                          setSelectedGraph(nextFilter);
                        }}
                        value={selectedGraph}
                        name="daysFilter"
                        style={{
                          border: "none",
                          outline: "none",
                          background: "none",
                        }}
                      >
                        <option value="last7DaysEarnings">Last 7 Days</option>
                        <option value="last14DaysEarnings">Last 14 Days</option>
                        <option value="last30DaysEarnings">Last 30 Days</option>
                        <option value="last60DaysEarnings">Last 60 Days</option>
                        <option value="last6MonthEarnings">
                          Last 6 Months
                        </option>
                        <option value="lastYearEarnings">Last Year</option>
                        <option value="YTD">YTD</option>
                        <option value="custom">Custom</option>
                      </select>
                      {selectedGraph === "custom" && (
                        <>
                          <label htmlFor="financeGraphFromDate">From</label>
                          <input
                            id="financeGraphFromDate"
                            aria-label="Finance graph from date"
                            min={"2023-01-01"}
                            type="date"
                            className="mx-2"
                            value={graphFromDate}
                            max={graphToDate || businessToday}
                            onChange={(e) => {
                              setGraphFromDate(e.target.value);
                            }}
                            style={{
                              border: "none",
                              outline: "none",
                              background: "none",
                            }}
                          />
                          <label htmlFor="financeGraphToDate">To</label>
                          <input
                            id="financeGraphToDate"
                            aria-label="Finance graph to date"
                            type="date"
                            className="mx-2 "
                            value={graphToDate}
                            min={graphFromDate}
                            max={businessToday}
                            onChange={(e) => {
                              setGraphToDate(e.target.value);
                            }}
                            style={{
                              border: "none",
                              outline: "none",
                              background: "none",
                            }}
                          />
                        </>
                      )}
                    </div>
                    <div>
                      {financeSummary?.asOf
                        ? `As of ${moment(financeSummary.asOf)
                            .tz(financeSummary.timezone)
                            .format("MMM D, YYYY h:mm A z")}`
                        : "Filter"}
                    </div>
                  </div>
                  <div
                    className={
                      financeSummary?.reconciliation?.dataQuality === "complete"
                        ? "text-success"
                        : "text-warning"
                    }
                  >
                    Data quality:{" "}
                    {financeSummary?.reconciliation?.dataQuality ||
                      "unavailable"}
                    .
                    {financeSummary?.metrics?.reconciliationVarianceCents ===
                      null &&
                      " The difference cannot be verified for this period; review the details below."}
                    {financeSummary?.reconciliation?.providerLedger && (
                      <p className="mb-1">Stripe ledger: {financeSummary.reconciliation.providerLedger.status}.
                        {financeSummary.reconciliation.providerLedger.stale && " Last verification is out of date."}
                        {financeSummary.reconciliation.providerLedger.lastSyncedAt && ` Last synced ${moment(financeSummary.reconciliation.providerLedger.lastSyncedAt).tz(businessTimezone).format("MMM D, YYYY h:mm A z")}.`}
                      </p>
                    )}
                    {financeSummary?.reconciliation?.accountingLinkage && <p className="mb-1">Transaction matching: {financeSummary.reconciliation.accountingLinkage.status}.</p>}
                    {reasons.length > 0 && <ul className="mb-0" aria-label="Reconciliation details">{reasons.map(({ code, message }) => <li key={code}>{message}</li>)}</ul>}
                  </div>
                </div>
              </div>
            </div>
            <div className="tw-flex tw-justify-between align-items-center mb-4">
              <div>
                <h1 className="text-lg font-bold mb-0">Commission activity</h1>
                <small>Search scope: exact reservation ID (table only)</small>
                <p className="mb-0">{activityRange.fromDate ? `Activity dates: ${activityRange.fromDate} through ${activityRange.toDate}` : "All history"}. Independent of the revenue chart.</p>
              </div>
              <div
                className="input-group search-area"
                style={{
                  boxShadow: "0px 4px 18px 0px rgba(75, 70, 92, 0.1)",
                  // maxWidth: 350,
                }}
              >
                <input
                  type="text"
                  className="form-control"
                  aria-label="Exact reservation ID"
                  placeholder="Exact reservation ID"
                  value={searchValue}
                  onChange={handleOnSearch}
                />
                <span className="input-group-text">
                  <span aria-hidden="true">
                    <i className="flaticon-381-search-2"></i>
                  </span>
                </span>
              </div>
            </div>

            <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
              <label htmlFor="activityDateFilter">Activity dates</label>
              <select id="activityDateFilter" value={activityDateFilter} onChange={(event) => {
                setActivityDateFilter(event.target.value);
                if (event.target.value === "all") { setPage(1); setActivityRange({}); }
              }}><option value="all">All history</option><option value="custom">Custom dates</option></select>
              {activityDateFilter === "custom" && <>
                <label htmlFor="activityFromDate">From</label><input id="activityFromDate" type="date" value={activityFromDate} max={activityToDate || businessToday} onChange={(event) => setActivityFromDate(event.target.value)} />
                <label htmlFor="activityToDate">To</label><input id="activityToDate" type="date" value={activityToDate} min={activityFromDate} max={businessToday} onChange={(event) => setActivityToDate(event.target.value)} />
                <button className="btn btn-secondary btn-sm" onClick={applyActivityDates}>Apply activity dates</button>
              </>}
              <button className="btn btn-link" onClick={refreshFinanceSummary} disabled={activityLoading}>Refresh activity</button>
              {activity?.asOf && <small>Activity as of {moment(activity.asOf).tz(activity.timezone).format("MMM D, YYYY h:mm A z")}</small>}
            </div>
            {activityError && <div role="alert" className="alert alert-warning">Commission activity unavailable. {activityError} <button className="btn btn-link" onClick={refreshFinanceSummary}>Retry activity</button></div>}
            <div className="table-responsive">
              <table
                className="table rounded-1   display mb-4 dataTablesCard booking-table room-list-tbl table-responsive-lg "
                id="guestTable-all"
              >
                <thead>
                  <tr>
                    <th>Id</th>
                    <th>Reservation Id</th>
                    <th>Type</th>
                    <th>Guest</th>
                    <th>Host</th>
                    <th>Date</th>
                    <th>Guest Commission</th>
                    <th>Host Commission</th>
                    <th>Processing Fees</th>
                    <th>Final Amount</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                {!isLoadingSearchResult && !activityLoading ? (
                  <>
                    <tbody>
                      {activityError ? <tr><td colSpan="12" className="text-center">Activity could not be loaded.</td></tr> : commissions.length === 0 && (
                        <tr>
                          <td colSpan="12" className="text-center px-2 py-2">
                            No commission activity matches these filters.
                          </td>
                        </tr>
                      )}
                      {!activityError && commissions.length > 0 &&
                        commissions.map((curr, id) => (
                          <tr key={curr.id}>
                            <td>{(page - 1) * (activityPagination.limit || 10) + id + 1}</td>

                            <td>{curr.reservationId || "N/A"}</td>

                            <td>
                              {curr.type === "HostPenalty"
                                ? "Host Penalty"
                                : curr.type}{" "}
                            </td>
                            <td>{curr.guestName}</td>
                            <td>{curr.hostName}</td>
                            <td>
                              {curr.occurredAt ? moment(curr.occurredAt)
                                .tz(activity.timezone)
                                .format("MMMM Do, YYYY") : "Unavailable"}
                            </td>
                            <td
                              className={`text-center ${
                                curr.serviceFeeCents < 0
                                  ? "text-danger"
                                  : "text-success"
                              }`}
                            >
                              {formatCents(
                                curr.serviceFeeCents,
                                financeSummary?.currency
                              )}
                            </td>
                            <td
                              className={`text-center ${
                                curr.crewmateCommissionCents < 0
                                  ? "text-danger"
                                  : "text-success"
                              }`}
                            >
                              {formatCents(
                                curr.crewmateCommissionCents,
                                financeSummary?.currency
                              )}
                            </td>
                            <td
                              className={`text-center ${
                                curr.processingFeesCents < 0
                                  ? "text-danger"
                                  : "text-success"
                              }`}
                            >
                              {formatCents(
                                Number.isSafeInteger(curr.processingFeesCents)
                                  ? -Math.abs(curr.processingFeesCents)
                                  : null,
                                financeSummary?.currency
                              )}
                            </td>
                            <td
                              className={`text-center ${
                                curr.finalAmountCents < 0
                                  ? "text-danger"
                                  : "text-success"
                              }`}
                            >
                              {formatCents(
                                curr.finalAmountCents,
                                financeSummary?.currency
                              )}
                            </td>

                            <td className="text-center text-success">
                              {curr.type === "Refund" && (
                                <div className="bg-danger-light me-1 px-2 py-1 rounded-1 text-danger">
                                  Refund to Guest
                                </div>
                              )}
                              {curr.type === "Earnings" && (
                                <div className="bg-success-light me-1 px-2 py-1 rounded-1 text-success">
                                  Added to Wallet
                                </div>
                              )}

                              {curr.type === "HostPenalty" && (
                                <div className="bg-success-light me-1 px-2 py-1 rounded-1 text-success">
                                  Added to Wallet
                                </div>
                              )}

                              {curr.type === "Subscription" && (
                                <div className="bg-success-light me-1 px-2 py-1 rounded-1 text-success">
                                  Added to Wallet
                                </div>
                              )}
                            </td>
                            
                            {/* --- ACTION COLUMN --- */}
                            <td>
                              {(curr.type === "Earnings" || curr.type === "Subscription") && (
                                <button 
                                  className="btn btn-danger btn-xs shadow btn-sm"
                                  onClick={() => openRefundModal(curr)}
                                  disabled={
                                    refundActionsUnavailable ||
                                    !curr.id ||
                                    !Number.isSafeInteger(curr.finalAmountCents) ||
                                    !Number.isSafeInteger(curr.processingFeesCents)
                                  }
                                  title={
                                    refundActionsUnavailable ||
                                    !curr.id ||
                                    !Number.isSafeInteger(curr.finalAmountCents) ||
                                    !Number.isSafeInteger(curr.processingFeesCents)
                                      ? "Finance record is incomplete"
                                      : undefined
                                  }
                                >
                                  Partial Refund
                                </button>
                              )}
                            </td>

                            {/* <td className="">
                        {curr.paidDate
                          ? moment(curr.paidDate).format("MMMM Do, YYYY")
                          : "--"}
                      </td> */}
                          </tr>
                        ))}
                    </tbody>
                  </>
                ) : (
                  <tbody>
                    <tr>
                      <td colSpan="12">{"Loading..."}</td>
                    </tr>
                  </tbody>
                )}
              </table>
            </div>

            {/* <div className="pagination"> */}
            <ul className="pagination justify-content-center">
              <li className="page-item">
                <button
                  onClick={handlePrevPage}
                  className="page-link"
                  disabled={activityLoading || isLoadingSearchResult || page <= 1}
                >
                  Previous
                </button>
              </li>
              <li className="page-item">
                <button
                  onClick={handleNextPage}
                  className="page-link"
                  disabled={
                    activityLoading || isLoadingSearchResult || Boolean(activityError) ||
                    !activityPagination.totalPages ||
                    page >= activityPagination.totalPages
                  }
                >
                  Next
                </button>
              </li>
            </ul>
            {/* </div> */}
          </>

        {/* --- MODAL IMPLEMENTATION --- */}
        {showRefundModal && (
          <div
            className="modal fade show"
            style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
            tabIndex="-1"
            role="dialog"
          >
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Process Partial Refund</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeRefundModal}
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body">
                  {refundStep === 1 ? (
                    <>
                      <p>{"Please enter the refund amount"}</p>
                      <p className="text-muted text-sm">
                        Recorded source amount (server revalidates):{" "}
                        <strong>
                          {formatCents(
                            selectedRefundableCents,
                            financeSummary?.currency
                          )}
                        </strong>{" "}
                        ({formatCents(
                          selectedRefundItem?.finalAmountCents,
                          financeSummary?.currency
                        )}{" "}
                        final amount;{" "}
                        {formatCents(
                          selectedRefundItem?.processingFeesCents,
                          financeSummary?.currency
                        )}{" "}
                        processing fees)
                      </p>
                      <div className="form-group mb-3">
                        <label htmlFor="refundAmount" className="form-label">
                          Refund Amount ($)
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          id="refundAmount"
                          placeholder="e.g. 10.50"
                          value={refundAmount}
                          onChange={handleRefundAmountChange}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="alert alert-warning">
                        <h6 className="alert-heading fw-bold">Are you sure?</h6>
                        <p className="mb-0">
                          You are about to refund <strong>${refundAmount}</strong>.
                          This action cannot be undone immediately.
                        </p>
                      </div>
                    </>
                  )}
                  {refundError && (
                    <div className="alert alert-danger py-2">{refundError}</div>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeRefundModal}
                    disabled={isSubmittingRefund}
                  >
                    Cancel
                  </button>
                  
                  {refundStep === 1 ? (
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleProceedToConfirm}
                      disabled={refundActionsUnavailable}
                    >
                      Review Refund
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={handleSubmitRefund}
                      disabled={isSubmittingRefund || refundActionsUnavailable}
                    >
                      {isSubmittingRefund ? "Processing..." : "Confirm Refund"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </Wrapper>
    </div>
  );
}

export default Earnings;
